/** @module store/log_events */
import * as apiLogEvents from '@/api/log_events'

const state = () => ({
  emsLogEvents: {}
})

const mutations = {
  SET_LOG_EVENTS: (state, { emsLogSourceToLogEvents }) => {
    state.emsLogEvents = emsLogSourceToLogEvents
  },
  SET_LATEST_LOG_EVENTS: (state, { logEvents }) => {
    if (!logEvents) return

    for (const logEvent of logEvents) {
      if (!(logEvent instanceof LogEvent)) return

      if (!state.emsLogEvents[logEvent.energyManagementSystemId])
        state.emsLogEvents[logEvent.energyManagementSystemId] = {}
      const currentLogEvent =
        state.emsLogEvents[logEvent.energyManagementSystemId][logEvent.logSource]?.[
          state.emsLogEvents[logEvent.energyManagementSystemId][logEvent.logSource].length - 1
        ]

      if (!currentLogEvent) state.emsLogEvents[logEvent.energyManagementSystemId][logEvent.logSource] = []
      else if (logEvent.datetime.isBefore(currentLogEvent.datetime)) return

      state.emsLogEvents[logEvent.energyManagementSystemId][logEvent.logSource].pop()
      state.emsLogEvents[logEvent.energyManagementSystemId][logEvent.logSource].push(logEvent)
    }
  }
}

const getters = {
  getLatestActiveStrategy: (state) => (emsId) => {
    const emsLogEvents = state.emsLogEvents[emsId]?.ems
    if (!emsLogEvents) return

    const activeStrategyLogEvents = emsLogEvents.filter((logEvent) => logEvent.message?.includes('- Active strategy:'))
    if (!activeStrategyLogEvents.length) return

    const latestActiveStrategyLogEvent = activeStrategyLogEvents
      .sort((a, b) => moment(a.datetime).diff(b.datetime))
      .pop()

    const activeStrategyMatch = latestActiveStrategyLogEvent.message?.match(/- Active strategy: (.+)/)
    if (!activeStrategyMatch) return

    return {
      activeStrategy: activeStrategyMatch[1],
      datetime: latestActiveStrategyLogEvent.datetime
    }
  },
  getLatestLogSourceStatus: (state) => (emsId) =>
    Object.entries(state.emsLogEvents[emsId] || []).reduce((prev, [logSource, logEvents]) => {
      const latestStatus = logEvents[logEvents.length - 1].status()

      return latestStatus === 'ok' ? prev : { ...prev, [logSource]: latestStatus }
    }, {}),
  getAggregatedLogSourcesStatus: (state) => (emsId) => {
    const logSourceStatuses = Object.values(state.emsLogEvents[emsId] || {}).map((logEvents) =>
      logEvents[logEvents.length - 1].status()
    )

    if (logSourceStatuses.includes('alarm')) return 'alarm'
    if (logSourceStatuses.includes('warn')) return 'warn'
    return 'ok'
  }
}

const actions = {
  getLogEvents: ({ commit }, { ctxt, getLatestOnly }) => {
    const params = getLatestOnly
      ? { dateTo: moment().toISOString(), latestLogEventPerLogSource: true, period: 'day' }
      : {}

    return apiLogEvents.getLogEvents({ ctxt, ...params }).then((msg) => {
      const logEvents = msg?.payload?.data
        ?.map((logEventResource) => resourceToLogEvents(logEventResource))
        .reduce((prev, logEvents) => [...prev, ...logEvents], [])

      const emsLogSourceToLogEvents = logEvents.reduce((prev, logEvent) => {
        prev[logEvent.energyManagementSystemId] ||= {}
        prev[logEvent.energyManagementSystemId][logEvent.logSource] ||= []
        prev[logEvent.energyManagementSystemId][logEvent.logSource].push(logEvent)
        prev[logEvent.energyManagementSystemId][logEvent.logSource].sort((a, b) => moment(a).diff(b))

        return prev
      }, {})

      commit('SET_LOG_EVENTS', {
        emsLogSourceToLogEvents
      })
    })
  },
  updateLogEvents: ({ commit }, { logEventResources }) => {
    for (const logEventResource of logEventResources) {
      commit('SET_LATEST_LOG_EVENTS', {
        logEvents: resourceToLogEvents(logEventResource)
      })
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}

export class LogEvent {
  constructor({ datetime, energyManagementSystemId, logSource, message }) {
    if (!energyManagementSystemId) throw new TypeError('An EMS-ID has to be present')
    if (!logSource) throw new TypeError('A LogSource has to be present')

    this.datetime = moment(datetime)
    this.energyManagementSystemId = parseInt(energyManagementSystemId)
    this.logSource = logSource
    this.message = message
  }

  status() {
    if (this.message.startsWith('OK') || (this.logSource === 'ems' && this.message === 'startup')) return 'ok'
    if (this.message.startsWith('ALARM')) return 'alarm'
    return 'warn'
  }
}

const resourceToLogEvents = (resource) => {
  const energyManagementSystemId = resource?.relationships?.energyManagementSystem?.data?.id || resource.id

  if (!energyManagementSystemId) return

  return resource?.attributes?.logSources.map((logSource, i) => {
    return new LogEvent({
      datetime: resource?.attributes?.datetimes[i],
      energyManagementSystemId,
      logSource,
      message: resource?.attributes?.messages[i]
    })
  })
}
